import { Component, Optional, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@services/auth/auth.service';
import { type AuthChoice, AuthFormComponent } from '@shared/components/auth-form/auth-form.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

/** A quién se dirige una pantalla de acceso. `null` = la puerta genérica. */
export type AccessAudience = 'candidate' | 'company';

/**
 * Pantalla de acceso. **Tres puertas, un componente.**
 *
 * - `/login` — la de siempre. Formulario directo, sin cajas.
 * - `/access/candidates` — «Inicia sesión» + «Alta candidatos».
 * - `/access/companies` — «Inicia sesión» + «Alta empresas».
 *
 * Las dos de audiencia se distinguen por `data.audience` de la ruta, no por un
 * componente propio: lo que cambia entre ellas son dos textos y a dónde lleva la
 * segunda caja. Todo lo demás —MFA, correo sin verificar, el mapeo de errores
 * del backend, el atajo de dev en localhost— es exactamente el mismo camino, y
 * duplicarlo habría significado arreglar el próximo fallo de login dos veces.
 *
 * **Por qué `/login` sigue existiendo, y sin cajas.** Es la puerta del equipo
 * interno, y el equipo interno no tiene alta: enseñarle «Alta candidatos» sería
 * ofrecerle algo que no le sirve. Además es la URL que ya está escrita en medio
 * sitio — la devuelve `requireSession()` cuando se pierde la sesión, la usan los
 * correos, el botón «volver al acceso» del cambio de contraseña y cualquier
 * enlace que alguien tenga guardado. Redirigirla a una de las nuevas obligaría a
 * elegir por el visitante: a un administrador al que se le caduca la sesión se
 * le abriría una pantalla que le habla de darse de alta como candidato.
 */
@Component({
	selector: 'app-login',
	imports: [AuthFormComponent],
	templateUrl: './login.component.html',
	styleUrl: './login.component.scss',
})
export class LoginComponent {
	isRegister: 1 | 2 | 3 | 4 = 1; // 1 = Login, 4 = MFA
	errorMessage: string | null = null;
	isLoading = false;

	// MFA state
	mfaAccessToken: string | null = null;
	mfaFactorId: string | null = null;

	/** Sale de la ruta. `null` en `/login` y cuando se abre como diálogo. */
	readonly audience: AccessAudience | null = null;

	/**
	 * `choices` mientras se enseñan las dos cajas; `login` una vez elegida la
	 * primera. No es una ruta aparte a propósito: el navegador no cambia de
	 * página, así que compartir la URL de `/access/candidates` lleva siempre al
	 * mismo sitio y no a mitad de un formulario.
	 */
	readonly stage = signal<'choices' | 'login'>('login');

	constructor(
		private authService: AuthService,
		private translocoService: TranslocoService,
		private router: Router,
		route: ActivatedRoute,
		@Optional() public ref: DynamicDialogRef,
	) {
		const audience = route.snapshot.data['audience'];
		if (audience === 'candidate' || audience === 'company') {
			this.audience = audience;
			this.stage.set('choices');
			const scope = audience === 'candidate' ? 'candidates' : 'companies';
			this.choiceList = [
				{
					key: 'login',
					icon: 'LogIn',
					titleKey: 'auth.access.login.title',
					descriptionKey: `auth.access.${scope}.loginDescription`,
					ctaKey: 'auth.access.login.cta',
				},
				{
					key: 'signup',
					icon: 'UserPlus',
					titleKey: `auth.access.${scope}.signupTitle`,
					descriptionKey: `auth.access.${scope}.signupDescription`,
					ctaKey: 'auth.access.signup.cta',
				},
			];
		}
	}

	/**
	 * Las dos cajas, construidas una sola vez.
	 *
	 * No es un `get` que devuelva un array nuevo: `[choices]` se evalúa en cada
	 * ciclo de detección de cambios, y un array distinto cada vez obliga al `@for`
	 * a recalcularse continuamente sobre una pantalla que no se mueve.
	 */
	private readonly choiceList: AuthChoice[] | null = null;

	/** Las cajas, o `null` cuando ya se está dentro del formulario. */
	get choices(): AuthChoice[] | null {
		return this.stage() === 'choices' ? this.choiceList : null;
	}

	get choicesTitleKey(): string {
		return this.audience === 'company' ? 'auth.access.companies.title' : 'auth.access.candidates.title';
	}

	get choicesSubtitleKey(): string {
		return this.audience === 'company' ? 'auth.access.companies.subtitle' : 'auth.access.candidates.subtitle';
	}

	/** El «volver» solo tiene sentido si hay cajas detrás a las que volver. */
	get backLabelKey(): string | null {
		return this.audience && this.stage() === 'login' ? 'auth.access.back' : null;
	}

	backToChoices(): void {
		this.errorMessage = null;
		this.stage.set('choices');
	}

	/**
	 * El alta NO se pinta aquí dentro: se navega a su propia URL.
	 *
	 * Las dos altas son pantallas completas con su propia carcasa — la de
	 * candidato son 26 campos con índice lateral y tres zonas de subida, y la de
	 * empresa trae su propia cabecera y su aviso legal. Meterlas en la columna de
	 * 380 px del formulario de acceso las dejaría inservibles en móvil y
	 * obligaría a mantener dos maquetaciones de cada una. Y hay una razón mejor:
	 * un alta a medias tiene que poder recuperarse volviendo a la URL, y una
	 * pantalla que cambia de contenido sin cambiar de dirección no lo permite.
	 */
	onChoice(key: string): void {
		if (key === 'signup') {
			this.router.navigate([this.audience === 'company' ? '/company-signup' : '/candidate-signup']);
			return;
		}
		this.stage.set('login');
	}

	onLogin(formData: {
		email?: string;
		password?: string;
		mfaCode?: string;
		trustDevice?: boolean;
		devLogin?: boolean;
	}): void {
		this.errorMessage = null;

		// Handle MFA verification
		if (this.isRegister === 4 && formData.mfaCode) {
			this.onVerifyMFA(formData.mfaCode, formData.trustDevice || false);
			return;
		}

		// Handle localhost-only dev login (no password)
		if (formData.devLogin && formData.email) {
			this.isLoading = true;
			this.authService.devLogin(formData.email).subscribe({
				next: () => {
					// Navigation handled by the auth service
				},
				error: (err) => {
					this.errorMessage = this.resolveAuthError(err);
					this.isLoading = false;
				},
			});
			return;
		}

		// Handle regular login
		if (!formData.email || !formData.password) {
			this.errorMessage = this.translocoService.translate('auth.errors.emailAndPasswordRequired');
			return;
		}

		const { email, password } = formData;

		this.isLoading = true;
		this.authService.login(email, password).subscribe({
			next: (response) => {
				// Check if MFA is required
				if (response.mfa_required) {
					this.isRegister = 4; // Switch to MFA mode
					this.mfaAccessToken = response.access_token;
					this.mfaFactorId = response.factor_id;
					this.isLoading = false;
				}
				// If no MFA, navigation is handled by auth service
			},
			error: (err) => {
				// An unverified address is not a dead end: the verify-email screen
				// already knows how to resend the link, so send them there with
				// the address prefilled instead of leaving a message they can't act on.
				if (this.authErrorCode(err) === 'AUTH_EMAIL_NOT_VERIFIED') {
					this.isLoading = false;
					this.router.navigate(['/verify-email'], { queryParams: { pending: 'true', email } });
					return;
				}
				this.errorMessage = this.resolveAuthError(err);
				this.isLoading = false;
			},
		});
	}

	private authErrorCode(err: unknown): string | undefined {
		return (err as { error?: { code?: string } })?.error?.code;
	}

	/**
	 * Maps the backend `{ code, message }` payload to a translated message.
	 * Backend strings stay in English (project convention: UI strings live
	 * in es.json, not in code). We translate by stable code; if no code
	 * matches, we fall back to the raw backend message so we don't swallow
	 * unexpected errors.
	 */
	private resolveAuthError(err: unknown): string {
		const code = (err as { error?: { code?: string } })?.error?.code;
		const backendMessage =
			(err as { error?: { message?: string }; message?: string })?.error?.message ??
			(err as { message?: string })?.message;

		const codeMap: Record<string, string> = {
			AUTH_USER_NOT_REGISTERED: 'auth.errors.userNotRegistered',
			AUTH_USER_NO_ROLE: 'auth.errors.userNoRole',
			AUTH_INVALID_CREDENTIALS: 'auth.errors.invalidCredentials',
			AUTH_EMAIL_NOT_VERIFIED: 'auth.errors.emailNotVerified',
			AUTH_INVALID_MFA_CODE: 'auth.login.mfa.invalidCode',
			AUTH_SESSION_FAILED: 'auth.errors.sessionFailed',
			// El proveedor no dijo que la contraseña esté mal: dijo que ahora no
			// puede comprobarla. Enseñar "credenciales inválidas" aquí manda a la
			// persona a cambiar una contraseña que era correcta.
			AUTH_RATE_LIMITED: 'auth.errors.rateLimited',
			AUTH_PROVIDER_UNAVAILABLE: 'auth.errors.providerUnavailable',
			AUTH_GENERIC: 'auth.errors.unknownError',
		};

		const i18nKey = code ? codeMap[code] : null;
		if (i18nKey) return this.translocoService.translate(i18nKey);
		return backendMessage || this.translocoService.translate('auth.errors.unknownError');
	}

	onVerifyMFA(mfaCode: string, trustDevice: boolean): void {
		if (!mfaCode || !this.mfaAccessToken || !this.mfaFactorId) {
			this.errorMessage = this.translocoService.translate('auth.login.mfa.invalidCode');
			return;
		}

		this.isLoading = true;
		this.authService.verifyMFALogin(this.mfaAccessToken, this.mfaFactorId, mfaCode, trustDevice).subscribe({
			next: () => {
				// Reset MFA state - navigation is handled by auth service
				this.isRegister = 1;
				this.mfaAccessToken = null;
				this.mfaFactorId = null;
				this.errorMessage = null;
			},
			error: () => {
				this.errorMessage = this.translocoService.translate('auth.login.mfa.invalidCode');
				this.isLoading = false;
			},
		});
	}

	cancelMFA(): void {
		this.isRegister = 1;
		this.mfaAccessToken = null;
		this.mfaFactorId = null;
		this.errorMessage = null;
	}
}
